import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  ChevronRight,
  Search,
  ArrowUpDown,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { StationCard } from '@/components/StationCard';
import { OrphanedStationSheet } from '@/features/stations/components/OrphanedStationSheet';
import {
  useAllStationsErrors,
  useUnassignedStations,
  useDeleteOrphanedStation,
} from '@/features/legacy/hooks/useStations';
import type { StationErrorInfo, OrphanedStationsInfo, Station, ClientStationsInfo, UnassignedStation } from '@/api/legacy';

type SortMode = 'name' | 'id' | 'errors' | 'stations';

const SORT_LABELS: Record<SortMode, string> = {
  name: 'По имени',
  id: 'По ID',
  errors: 'По ошибкам',
  stations: 'По станциям',
};

/**
 * Russian pluralization helper
 * @param n - number
 * @param one - form for 1 (станция)
 * @param few - form for 2-4 (станции)
 * @param many - form for 5-20 (станций)
 */
function pluralize(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 19) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

interface ClientStationsData {
  sobid: string;
  clientName: string;
  stations: { name: string; info: StationErrorInfo }[];
  hasErrors: boolean;
  errorCount: number;
}

function isOrphanedInfo(value: unknown): value is OrphanedStationsInfo {
  if (value === null || typeof value !== 'object') return false;
  // OrphanedStationsInfo specifically has 'issue' array property
  return 'issue' in (value as object);
}

function isClientStationsInfo(value: unknown): value is ClientStationsInfo {
  if (value === null || typeof value !== 'object') return false;
  return 'clientName' in (value as object) && 'stations' in (value as object);
}

interface StationWithSobid extends Station {
  displayName: string;
  sobid: number;
}

interface ClientCardProps {
  client: ClientStationsData;
  onNavigate: (sobid: string) => void;
  onStationClick: (station: StationWithSobid) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}

function ClientCard({ client, onNavigate, onStationClick, onRefresh, isRefreshing }: ClientCardProps) {
  const hasProblems = client.hasErrors || client.errorCount > 0;

  return (
    <Collapsible className="border rounded-lg overflow-hidden">
      <div className="flex items-center">
        <CollapsibleTrigger className="flex-1 p-3 flex items-center gap-3 hover:bg-muted/50 transition-colors">
          <div
            className={`w-2 h-2 rounded-full shrink-0 ${
              hasProblems ? 'bg-red-500' : 'bg-green-500'
            }`}
          />
          <div className="flex-1 min-w-0 text-left">
            <div className="font-medium text-sm truncate">{client.clientName}</div>
            <div className="text-xs text-muted-foreground">
              {client.stations.length} {pluralize(client.stations.length, 'станция', 'станции', 'станций')}
              {client.errorCount > 0 && `, ${client.errorCount} с ошибками`}
            </div>
          </div>
        </CollapsibleTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 mr-2"
          onClick={() => onNavigate(client.sobid)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <CollapsibleContent>
        <div className="px-3 pb-3 space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
          {client.stations.map(({ name, info }) => (
            <StationCard
              key={info.id}
              station={{
                displayName: name,
                errors: info.errors,
                critical: info.critical,
                lastreport: info.lastreport,
                ports: info.ports,
                id: info.id,
              }}
              onClick={() => onStationClick({
                name,
                displayName: name,
                errors: info.errors,
                critical: info.critical,
                lastreport: info.lastreport,
                ports: info.ports,
                portcheck: info.portcheck || '',
                id: info.id,
                sobid: parseInt(client.sobid, 10),
              })}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function StationsControlPage() {
  const navigate = useNavigate();
  const [selectedOrphanedStation, setSelectedOrphanedStation] = useState<UnassignedStation | null>(null);
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('errors');

  const { data, isLoading, error, refetch, isRefetching } = useAllStationsErrors();
  const { data: unassignedStations } = useUnassignedStations();
  const deleteOrphanedStation = useDeleteOrphanedStation();

  // Transform data into grouped structure
  const { clients, orphaned, stats } = useMemo(() => {
    if (!data) {
      return { clients: [], orphaned: null, stats: { totalClients: 0, totalStations: 0, totalErrors: 0, noStations: 0 } };
    }

    const clientsList: ClientStationsData[] = [];
    let totalStations = 0;
    let totalErrors = 0;
    let noStationsCount = 0;
    let orphanedData: OrphanedStationsInfo | null = null;

    for (const [key, value] of Object.entries(data)) {
      if (key === 'orphaned') {
        orphanedData = value as OrphanedStationsInfo;
        continue;
      }

      // Skip if it's orphaned info structure (not client data)
      if (isOrphanedInfo(value)) {
        continue;
      }

      // Handle new structure: { clientName, stations }
      let stationsRecord: Record<string, StationErrorInfo>;
      let clientName: string;

      if (isClientStationsInfo(value)) {
        // New format with clientName
        clientName = value.clientName;
        stationsRecord = value.stations;
      } else {
        // Fallback for old format (sobid -> stations directly)
        clientName = key;
        stationsRecord = value as Record<string, StationErrorInfo>;
      }

      const stationEntries = Object.entries(stationsRecord);

      if (stationEntries.length === 0) {
        noStationsCount++;
        continue;
      }

      const stations = stationEntries.map(([name, info]) => ({ name, info }));
      totalStations += stations.length;

      let errorCount = 0;
      let hasErrors = false;

      for (const { info } of stations) {
        if (info.errors.trim() || info.critical.trim()) {
          errorCount++;
          hasErrors = true;
        }
        // Check if stale (> 3 days)
        const lastReportDate = info.lastreport ? new Date(info.lastreport) : null;
        if (lastReportDate) {
          const daysSince = Math.floor((Date.now() - lastReportDate.getTime()) / (1000 * 60 * 60 * 24));
          if (daysSince > 3) {
            errorCount++;
            hasErrors = true;
          }
        }
        // Check if station has critical issues (e.g. S.M.A.R.T errors)
        if (info.critical?.trim()) {
          errorCount++;
          hasErrors = true;
        }
      }

      totalErrors += errorCount;

      clientsList.push({
        sobid: key,
        clientName,
        stations,
        hasErrors,
        errorCount,
      });
    }

    return {
      clients: clientsList,
      orphaned: orphanedData,
      stats: {
        totalClients: clientsList.length,
        totalStations,
        totalErrors,
        noStations: noStationsCount,
      },
    };
  }, [data]);

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!search.trim()) return clients;
    const searchLower = search.toLowerCase();
    return clients.filter(
      (c) =>
        c.clientName.toLowerCase().includes(searchLower) ||
        c.sobid.includes(search)
    );
  }, [clients, search]);

  // Sort clients
  const sortedClients = useMemo(() => {
    const sorted = [...filteredClients];
    switch (sortMode) {
      case 'name':
        sorted.sort((a, b) => a.clientName.localeCompare(b.clientName, 'ru'));
        break;
      case 'id':
        sorted.sort((a, b) => parseInt(a.sobid, 10) - parseInt(b.sobid, 10));
        break;
      case 'errors':
        sorted.sort((a, b) => {
          // Errors first, then by error count desc
          if (a.hasErrors && !b.hasErrors) return -1;
          if (!a.hasErrors && b.hasErrors) return 1;
          return b.errorCount - a.errorCount;
        });
        break;
      case 'stations':
        sorted.sort((a, b) => b.stations.length - a.stations.length);
        break;
    }
    return sorted;
  }, [filteredClients, sortMode]);

  const handleNavigateToClient = (sobid: string) => {
    navigate(`/visits/client/${parseInt(sobid, 10)}`);
  };

  const handleStationClick = (station: StationWithSobid) => {
    navigate('/more/stations-control/station', { state: { station } });
  };

  const handleDeleteOrphaned = (stationId: string, comment: string) => {
    deleteOrphanedStation.mutate(
      { stationId, comment },
      {
        onSuccess: () => {
          setSelectedOrphanedStation(null);
        },
      }
    );
  };

  return (
    <div className="flex flex-col min-h-full">
      <Header
        title="Контроль РК"
        subtitle={data ? `${stats.totalErrors} ошибок` : undefined}
        showBack
        rightContent={
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <ArrowUpDown className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(Object.keys(SORT_LABELS) as SortMode[]).map((mode) => (
                  <DropdownMenuItem
                    key={mode}
                    onClick={() => setSortMode(mode)}
                    className={sortMode === mode ? 'bg-accent' : ''}
                  >
                    {SORT_LABELS[mode]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => refetch()}
              disabled={isRefetching}
            >
              <RefreshCw className={`h-5 w-5 ${isRefetching ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        }
      />

      {/* Search bar */}
      <div className="px-4 py-2 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск клиента..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-destructive font-medium">Ошибка загрузки</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mt-4"
            >
              Повторить
            </Button>
          </div>
        ) : (
          <>
            {/* Success banner when no errors */}
            {stats.totalErrors === 0 && !orphaned?.issue?.length && (
              <div className="flex items-center gap-3 p-3 mb-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                <span className="text-sm text-green-800 dark:text-green-200">Ошибок резервного копирования не обнаружено</span>
              </div>
            )}

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className={`p-3 rounded-lg ${stats.totalErrors > 0 ? 'bg-destructive/10 border border-destructive/20' : 'bg-muted'}`}>
                <div className={`text-xl font-bold ${stats.totalErrors > 0 ? 'text-destructive' : ''}`}>{stats.totalErrors}</div>
                <div className="text-xs text-muted-foreground">Ошибок</div>
              </div>
              <div className="p-3 rounded-lg bg-muted">
                <div className="text-xl font-bold">{stats.totalClients}</div>
                <div className="text-xs text-muted-foreground">Клиентов</div>
              </div>
              <div className="p-3 rounded-lg bg-muted">
                <div className="text-xl font-bold">{stats.totalStations}</div>
                <div className="text-xs text-muted-foreground">Станций</div>
              </div>
            </div>

            {/* Orphaned stations - clickable list */}
            {unassignedStations && unassignedStations.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-sm text-amber-800 dark:text-amber-200">
                    Потерянные станции ({unassignedStations.length})
                  </span>
                </div>
                <div className="space-y-1">
                  {unassignedStations.map((station) => (
                    <button
                      key={station.clientPublicKey}
                      className="w-full p-2 text-left rounded bg-amber-100 dark:bg-amber-900 hover:bg-amber-200 dark:hover:bg-amber-800 transition-colors flex items-center justify-between"
                      onClick={() => setSelectedOrphanedStation(station)}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-amber-900 dark:text-amber-100 truncate">
                          {station.computername || station.comment || `ID: ${station.id}`}
                        </div>
                        <div className="text-xs text-amber-700 dark:text-amber-300 truncate">
                          {station.clientPublicKey.slice(0, 24)}...
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-amber-600 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No stations warning */}
            {stats.noStations > 0 && (
              <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <span className="text-sm text-orange-800 dark:text-orange-200">
                    {stats.noStations} клиентов без станций контроля
                  </span>
                </div>
              </div>
            )}

            {/* Clients list */}
            <div className="space-y-2">
              {sortedClients.length === 0 && search ? (
                <div className="text-center py-8 text-muted-foreground">
                  Клиенты не найдены
                </div>
              ) : (
                sortedClients.map((client) => (
                  <ClientCard
                    key={client.sobid}
                    client={client}
                    onNavigate={handleNavigateToClient}
                    onStationClick={handleStationClick}
                    onRefresh={() => refetch()}
                    isRefreshing={isRefetching}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Orphaned station detail sheet */}
      <OrphanedStationSheet
        station={selectedOrphanedStation}
        open={!!selectedOrphanedStation}
        onOpenChange={(open) => !open && setSelectedOrphanedStation(null)}
        onDelete={handleDeleteOrphaned}
        isDeleting={deleteOrphanedStation.isPending}
      />
    </div>
  );
}
